import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Building2, ShieldCheck, FileText, Users, MapPin,
  Mail, Phone, Edit2, Calendar, DollarSign, Clock,
} from "lucide-react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const TYPE_LABELS = {
  gobierno_federal: "Gobierno Federal",
  gobierno_estatal: "Gobierno Estatal",
  gobierno_municipal: "Gobierno Municipal",
  organismo_publico_descentralizado: "Organismo Público Descentralizado",
  privado_persona_fisica: "Privado · Persona Física",
  privado_persona_moral: "Privado · Persona Moral",
};

const STATUS_LABEL = {
  borrador: "Borrador", firmado: "Firmado", ejecucion: "En ejecución",
  concluido: "Concluido", rescindido: "Rescindido", cancelado: "Cancelado",
};
const STATUS_COLOR = {
  borrador: "#94a3b8", firmado: "#22d3ee", ejecucion: "#16a34a",
  concluido: "#3F2A91", rescindido: "#dc2626", cancelado: "#64748b",
};

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [cRes, sRes, ctRes, pRes] = await Promise.all([
          api.get(`/contracting-entities/${id}`),
          api.get(`/contracting-entities/${id}/stats`).catch(() => ({ data: null })),
          api.get(`/contracts`, { params: {} }).then((r) =>
            (r.data || []).filter((c) => c.contracting_entity_id === id),
          ).catch(() => []),
          api.get(`/projects`).catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setClient(cRes.data);
        setStats(sRes.data);
        setContracts(Array.isArray(ctRes) ? ctRes : []);
        const map = {};
        (pRes.data || []).forEach((p) => { map[p.id] = p; });
        setProjects(map);
      } catch (e) {
        setError(e.response?.data?.detail || "No se pudo cargar el cliente.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="p-10 text-center text-slate-400">Cargando…</div>;
  if (error || !client) {
    return (
      <div className="p-10 text-center text-slate-400">
        <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
        <div>{error || "Cliente no encontrado."}</div>
        <Link to="/contracting-entities" className="btn-ghost px-3 py-1.5 mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al catálogo
        </Link>
      </div>
    );
  }

  const projectIdsFromContracts = [...new Set(contracts.map((c) => c.project_id).filter(Boolean))];

  return (
    <div data-testid="client-detail-page">
      <PageHeader
        subtitle={TYPE_LABELS[client.type] || client.type}
        title={client.short_name ? `${client.short_name} — ${client.name}` : client.name}
        actions={
          <Link to="/contracting-entities" className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-5 max-w-6xl">
        <div className="flex items-center gap-2 flex-wrap">
          {client.auditable && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-sm"
              style={{ background: "rgba(217,70,239,0.15)", color: "var(--magenta)", border: "1px solid rgba(217,70,239,0.3)" }}>
              <ShieldCheck className="w-3 h-3" /> AUDITABLE · {client.audit_entity || "—"} · {client.audit_retention_years}a
            </span>
          )}
          {client.rfc && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono rounded-sm"
              style={{ background: "rgba(34,211,238,0.10)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.25)" }}>
              RFC: {client.rfc}
            </span>
          )}
        </div>

        {/* KPIs de stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="client-stats">
            <Kpi label="Contratos" value={stats.contracts_count || 0} />
            <Kpi label="Proyectos" value={stats.projects_count || 0} />
            <Kpi label="Contratado" value={fmtMXN(stats.total_contracted || 0)} />
            <Kpi label="Facturado" value={fmtMXN(stats.total_invoiced || 0)} />
            <Kpi
              label="Saldo por cobrar"
              value={fmtMXN(stats.balance || 0)}
              highlight
              tone={stats.balance > 0 ? "warn" : "ok"}
            />
          </div>
        )}

        {stats && (stats.estimaciones_count > 0 || stats.avg_pay_days !== null) && (
          <div className="panel p-4 flex items-center gap-4 flex-wrap text-xs">
            <div className="flex items-center gap-2 text-slate-300">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span>Días promedio de pago:</span>
              <b className="text-cyan-300 font-mono">
                {stats.avg_pay_days !== null && stats.avg_pay_days !== undefined ? `${stats.avg_pay_days} días` : "—"}
              </b>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <FileText className="w-3.5 h-3.5 text-slate-500" />
              <span>Estimaciones registradas:</span>
              <b className="text-slate-100">{stats.estimaciones_count || 0}</b>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <DollarSign className="w-3.5 h-3.5 text-slate-500" />
              <span>Pagado:</span>
              <b className="text-green-300">{fmtMXN(stats.total_paid || 0)}</b>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Datos generales */}
          <Box title="Datos del cliente">
            <div className="space-y-2 text-sm">
              {client.legal_representative && (
                <Line label={client.legal_representative_title || "Rep. Legal"} value={client.legal_representative} />
              )}
              {(client.phone || client.email) && (
                <div className="space-y-1.5">
                  {client.phone && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Phone className="w-3.5 h-3.5 text-slate-500" /> <span className="font-mono">{client.phone}</span>
                    </div>
                  )}
                  {client.email && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail className="w-3.5 h-3.5 text-slate-500" /> <span>{client.email}</span>
                    </div>
                  )}
                </div>
              )}
              {(client.address || client.city || client.state) && (
                <div className="flex items-start gap-2 text-slate-300">
                  <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
                  <div>
                    {client.address && <div>{client.address}</div>}
                    <div className="text-slate-400">{[client.city, client.state, client.cp].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              )}
              <div className="text-xs text-slate-500 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                IVA default: <b className="text-slate-300">{client.default_iva_pct}%</b>
              </div>
              {client.notes && (
                <div className="text-xs text-slate-400 pt-2 border-t italic" style={{ borderColor: "var(--border)" }}>
                  {client.notes}
                </div>
              )}
            </div>
          </Box>

          {/* Contactos */}
          <Box title="Personas de contacto" right={
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              {(client.contacts || []).length}
            </span>
          }>
            {(client.contacts || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic">Sin contactos registrados.</div>
            ) : (
              <div className="space-y-2">
                {client.contacts.map((c, i) => (
                  <div key={i} data-testid={`contact-${i}`} className="panel p-2.5">
                    <div className="font-semibold text-sm text-slate-100">{c.name}</div>
                    {c.title && <div className="text-[11px] text-cyan-300">{c.title}</div>}
                    <div className="flex flex-wrap gap-x-3 mt-1 text-[11px] text-slate-400">
                      {c.phone && <span className="font-mono">📞 {c.phone}</span>}
                      {c.email && <span>✉ {c.email}</span>}
                    </div>
                    {c.notes && <div className="text-[11px] text-slate-500 mt-1 italic">{c.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </Box>
        </div>

        {/* Contratos */}
        <Box title={`Historial de contratos (${contracts.length})`} right={
          <Link
            to="/contracts/new"
            data-testid="new-contract-from-client"
            className="btn-ghost px-2.5 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1"
            style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
          >
            + Nuevo contrato
          </Link>
        }>
          {contracts.length === 0 ? (
            <div className="text-xs text-slate-500 italic">Este cliente aún no tiene contratos registrados.</div>
          ) : (
            <div className="space-y-2">
              {contracts.map((c) => {
                const proj = projects[c.project_id];
                const color = STATUS_COLOR[c.status] || "#94a3b8";
                return (
                  <Link
                    key={c.id}
                    to={`/contracts/${c.id}`}
                    data-testid={`client-contract-${c.id}`}
                    className="panel p-3 block hover:border-cyan-500/60 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>
                          {proj ? `${proj.code} · ${proj.name}` : "Sin proyecto"}
                        </div>
                        <div className="font-mono font-bold text-sm text-slate-100 truncate mt-0.5">{c.contract_number}</div>
                        <div className="text-xs text-slate-400 line-clamp-1 mt-0.5">{c.description}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest"
                          style={{ background: `${color}20`, color, border: `1px solid ${color}55` }}>
                          {STATUS_LABEL[c.status] || c.status}
                        </span>
                        <div className="font-mono font-bold text-cyan-300 mt-1 text-sm">{fmtMXN(c.total)}</div>
                        {c.signing_date && (
                          <div className="text-[10px] text-slate-500 flex items-center gap-1 justify-end">
                            <Calendar className="w-3 h-3" /> {fmtDate(c.signing_date)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </Box>

        {/* Proyectos vinculados */}
        {projectIdsFromContracts.length > 0 && (
          <Box title={`Proyectos vinculados (${projectIdsFromContracts.length})`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {projectIdsFromContracts.map((pid) => {
                const p = projects[pid];
                if (!p) return null;
                return (
                  <Link
                    key={pid}
                    to={`/projects/${pid}/report`}
                    data-testid={`client-project-${pid}`}
                    className="panel p-3 hover:border-cyan-500/60 transition-colors"
                  >
                    <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>{p.code}</div>
                    <div className="font-bold text-sm text-slate-100 truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-400 mt-1">Avance: {p.progress || 0}%</div>
                  </Link>
                );
              })}
            </div>
          </Box>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight, tone }) {
  const color = tone === "warn" ? "#fbbf24" : tone === "ok" ? "#22d3ee" : "var(--cyan)";
  return (
    <div className="panel p-3">
      <div className="label-mono text-[10px]">{label}</div>
      <div className="font-mono font-bold text-base tabular-nums" style={{ color: highlight ? color : "#e2e8f0" }}>
        {value}
      </div>
    </div>
  );
}

function Box({ title, right, children }) {
  return (
    <div className="panel">
      <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <div className="font-display font-bold text-sm text-slate-200">{title}</div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="text-sm">
      <span className="text-slate-500">{label}: </span>
      <span className="text-slate-100">{value}</span>
    </div>
  );
}
